import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Modal, ModalDialog, ModalClose, 
  Grid, Card, Avatar, Chip, Divider, IconButton
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const LinksAdmin = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState({ title: '', url: '' });

  const loadLinks = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('quick_links').select('*').order('created_at', { ascending: false });
    if (!error) setLinks(data);
    setLoading(false);
  };

  useEffect(() => { loadLinks(); }, []);

  const handleSubmit = async () => {
    if (editingLink) {
      await supabase.from('quick_links').update(form).eq('id', editingLink.id);
    } else {
      await supabase.from('quick_links').insert(form);
    }
    setOpenModal(false);
    setEditingLink(null);
    setForm({ title: '', url: '' });
    loadLinks();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      await supabase.from('quick_links').delete().eq('id', id);
      loadLinks();
    }
  };

  // Helper functions
  const getLinkInitials = (title) => {
    return title?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'QL';
  };

  const getLinkColor = (index) => {
    const colors = ['#00A884', '#17a2b8', '#28a745', '#6c757d', '#ffc107', '#dc3545'];
    return colors[index % colors.length];
  };

  const getDomainFromUrl = (url) => {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return 'Invalid URL';
    }
  };

  // Statistics
  const totalLinks = links.length;
  const recentLinks = links.filter(l => {
    const created = new Date(l.created_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return created > sevenDaysAgo;
  }).length;
  const externalLinks = links.filter(l => !l.url.includes(window.location.hostname)).length;
  const internalLinks = totalLinks - externalLinks;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Table Loading */}
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Quick Links Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            link
          </span>
          Quick Links Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Links"
              value={totalLinks}
              icon="link"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Recent Links"
              value={recentLinks}
              icon="schedule"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="External Links"
              value={externalLinks}
              icon="open_in_new"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Internal Links"
              value={internalLinks}
              icon="home"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Link Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                add_link
              </span>
              Quick Links Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create and manage quick access links for users
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => { setForm({ title: '', url: '' }); setEditingLink(null); setOpenModal(true); }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Link
          </EnhancedButton>
        </Box>
      </Card>

      {/* Links Table */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            view_list
          </span>
          Quick Links Directory ({totalLinks})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'link_info', label: 'Link', width: 300 },
            { key: 'url_info', label: 'URL', width: 300 },
            { key: 'created', label: 'Created', width: 150 },
            { key: 'type', label: 'Type', width: 120 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}
          rows={links.map((link, index) => ({
            link_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getLinkColor(index),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getLinkInitials(link.title)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {link.title || 'Unnamed Link'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {getDomainFromUrl(link.url)}
                  </Typography>
                </Box>
              </Box>
            ),
            url_info: (
              <Box>
                <Typography 
                  component="a" 
                  href={link.url} 
                  target="_blank" 
                  rel="noreferrer"
                  level="body-sm" 
                  sx={{ 
                    color: 'primary.500', 
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 280
                  }}
                >
                  {link.url}
                </Typography>
              </Box>
            ),
            created: (
              <Typography level="body-sm">
                {new Date(link.created_at).toLocaleDateString()}
              </Typography>
            ),
            type: (
              <Chip 
                size="sm" 
                variant="soft" 
                color={link.url.includes(window.location.hostname) ? 'neutral' : 'primary'}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {link.url.includes(window.location.hostname) ? 'home' : 'open_in_new'}
                  </span>
                }
              >
                {link.url.includes(window.location.hostname) ? 'Internal' : 'External'}
              </Chip>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => { setEditingLink(link); setForm({ title: link.title, url: link.url }); setOpenModal(true); }}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDelete(link.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>

      {/* Enhanced Link Modal */}
      <Modal open={openModal} onClose={() => { setOpenModal(false); setEditingLink(null); }}>
        <ModalDialog 
          sx={{ 
            maxWidth: '500px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingLink ? 'edit' : 'add_link'}
            </span>
            {editingLink ? 'Edit Quick Link' : 'Add New Quick Link'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Link Title"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Documentation Portal"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    title
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <EnhancedInput
                label="URL"
                value={form.url}
                onChange={e => setForm({ ...form, url: e.target.value })}
                placeholder="https://example.com"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    link
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => { setOpenModal(false); setEditingLink(null); }}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editingLink ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editingLink ? 'Update Link' : 'Create Link'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default LinksAdmin;
